import { apiClient } from '../config/api';

export interface CaretakerProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  bio: string | null;
  phoneNumber: string | null;
  location: string | null;
  yearsExperience: number | null;
  hourlyRate: number | null;
  responseTime: string | null;
  languages: string[];
  // FEAT-047 — per-state agency verify link inputs (MVP-8.18, 8.35a).
  stateCode: string | null;
  stateLicenseNumber: string | null;
  specialties: Array<{
    id: string;
    name: string;
    description: string | null;
  }>;
  services: Array<{
    id: string;
    name: string;
    description: string | null;
    duration: number | null;
    price: number | null;
  }>;
  certifications: Array<{
    id: string;
    name: string;
    issuer: string;
    issueDate: string | null;
    expiryDate: string | null;
    credentialId: string | null;
    verified: boolean;
  }>;
  reviews: Array<{
    id: string;
    rating: number;
    comment: string | null;
    createdAt: string;
    patient: {
      firstName: string | null;
      lastName: string | null;
      avatar: string | null;
    };
  }>;
  availability: Array<{
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }>;
  averageRating: number;
  reviewCount: number;
  ratingBreakdown: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  hasRecentActivity: boolean;
  displayName: string;
  experienceText: string;
  hourlyRateText: string;
  languagesText: string;
  createdAt: string;
}

// FEAT-042 — neutral payload returned when viewer or target has marked
// Not a Fit on the pair. The page renders an unattributed empty state.
export interface CaretakerProfileUnavailable {
  id: string;
  available: false;
  notAvailableReason: 'profile_not_available';
  message: string;
}

export type CaretakerProfilePayload = CaretakerProfile | CaretakerProfileUnavailable;

export interface CaretakerProfileResponse {
  success: boolean;
  data: CaretakerProfilePayload;
  timestamp: string;
}

export const isCaretakerProfileUnavailable = (
  payload: CaretakerProfilePayload
): payload is CaretakerProfileUnavailable => {
  return (payload as CaretakerProfileUnavailable).available === false;
};

class CaretakerProfileService {
  /**
   * Get comprehensive caretaker profile data
   */
  async getCaretakerProfile(caretakerId: string): Promise<CaretakerProfilePayload> {
    try {
      const response = await apiClient.get<CaretakerProfileResponse>(
        `/caretakers/${caretakerId}/profile`
      );

      if (!response.data.success) {
        throw new Error('Failed to fetch caretaker profile');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching caretaker profile:', error);
      throw new Error('Failed to load caretaker profile');
    }
  }

  /**
   * Get basic caretaker data (for search results)
   */
  async getCaretakerBasic(caretakerId: string): Promise<Partial<CaretakerProfile>> {
    try {
      const response = await apiClient.get<CaretakerProfileResponse>(
        `/caretakers/${caretakerId}`
      );
      
      if (!response.data.success) {
        throw new Error('Failed to fetch caretaker data');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching caretaker basic data:', error);
      throw new Error('Failed to load caretaker data');
    }
  }

  /**
   * Contact caretaker (placeholder for future implementation)
   */
  async contactCaretaker(caretakerId: string, message: string): Promise<void> {
    try {
      // This would be implemented when messaging functionality is added
      console.log('Contacting caretaker:', caretakerId, message);
      throw new Error('Contact functionality not yet implemented');
    } catch (error) {
      console.error('Error contacting caretaker:', error);
      throw new Error('Failed to send message to caretaker');
    }
  }

  /**
   * Book appointment with caretaker (placeholder for future implementation)
   */
  async bookCaretaker(caretakerId: string, serviceId: string, dateTime: string): Promise<void> {
    try {
      // This would be implemented when booking functionality is added
      console.log('Booking caretaker:', caretakerId, serviceId, dateTime);
      throw new Error('Booking functionality not yet implemented');
    } catch (error) {
      console.error('Error booking caretaker:', error);
      throw new Error('Failed to book appointment');
    }
  }
}

export const caretakerProfileService = new CaretakerProfileService();
